<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { CONSENT_REQUEST_STATUS, CONSENT_REQUEST_TYPE } from "@core/constants"
import { coreServiceRegistry } from "@core/services/register"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { formatAddress, formatDate, formatIdSource } from "@core/helpers"
import type { BaseSummaryLine } from "@core/components/base/summary/BaseSummary.vue"
import type {
    SourceConsentSignSource,
    SourceConsentSignBuilding,
    SourceConsentSignRequestProps,
} from "@core/types/source/consent_request"
import type { OwnerOutput } from "@core/composables"
import {
    pickConsentExtendFieldsFromApiResponse,
    type ConsentExtendListPatchFields,
} from "@core/components/consent/consent.events"

// ================================
// TYPES & INTERFACES
// ================================

export interface SourceConsentSignProps {
    owner: OwnerOutput
    source: SourceConsentSignSource
    building: SourceConsentSignBuilding
    /** Date de début du consentement (ISO). Défaut : date du jour */
    dateFrom?: string
    request: SourceConsentSignRequestProps
    /**
     * Masque le titre du bandeau interne (« Signature du consentement »).
     * Utile lorsque le parent affiche déjà un titre (ex. parcours invitation).
     */
    hideHeaderTitle?: boolean
    /**
     * Masque le bouton « Retour » (parcours invitation sans navigation arrière).
     * Le bouton « Fermer » après signature reste affiché.
     */
    hideBackButton?: boolean
}

type SignedPayload = {
    consentId?: string
    sourceId?: string
    token?: string
    taskOnboarding?: unknown
    /** Après prolongement signé : champs consentement issus de la réponse API. */
    consentementFields?: ConsentExtendListPatchFields
}

/** Réponse partielle de création/mise à jour d'une demande de signature */
type ConsentRequestSignCreated = {
    _id?: string
    consentement?: { _id?: string }
    source?: { _id?: string }
    token?: string
    task_building?: unknown
    user_from?: { username?: string }
    user_to?: { username?: string }
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<SourceConsentSignProps>(), {
    dateFrom: () => new Date().toISOString().slice(0, 10),
    hideHeaderTitle: false,
    hideBackButton: false,
})

const emit = defineEmits<{
    back: []
    signed: [payload: SignedPayload]
    "click:quit": []
    /** true pendant l’appel API de signature / prolongation / changement de titulaire */
    "update:signing": [value: boolean]
}>()

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    consentRequest?: typeof coreServiceRegistry.consentRequest
}>("services")
const consentRequestService =
    injectedServices?.consentRequest ?? coreServiceRegistry.consentRequest

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Signature du consentement",
            submit: "Signer",
            back: "Retour",
            quitDone: "Fermer",
            loading:
                "Signature du consentement en cours, veuillez patienter...",
            loading_extend:
                "Prolongement du consentement en cours, veuillez patienter...",
            owner_label_pro: "Représentant",
            owner_label_particular: "Titulaire",
            owner_value_self: "{firstname} {lastname}",
            owner_value_company: "{firstname} {lastname}, pour {company}",
            address_label: "Adresse",
            idsource_label_pdl: "Numéro du Point De Livraison (PDL)",
            idsource_label_pce:
                "Numéro du Point de Comptage et d'Estimation (PCE)",
            start_date_label: "Date de début du consentement",
            date_from_data_label:
                "Date d'ouverture du contrat avec le fournisseur",
        },
    },
})

// ================================
// STATE
// ================================

const signed = ref(false)
const isDone = ref(false)

// ================================
// COMPUTED – résumé
// ================================

/**
 * Retourne le label du titulaire (Représentant ou Titulaire)
 */
const getOwnerLabel = computed(() =>
    props.owner?.company != null
        ? t("owner_label_pro")
        : t("owner_label_particular")
)

/**
 * Retourne la valeur du titulaire (Représentant ou Titulaire)
 * Si le titulaire est une entreprise, retourne le nom de l'entreprise
 * Sinon, retourne le nom du titulaire
 * Si le titulaire est le même que l'utilisateur connecté, ajoute "(vous)"
 */
const getOwnerValue = computed(() => {
    const firstname = props.owner?.user?.firstname ?? ""
    const lastname = props.owner?.user?.lastname ?? ""
    const company = props.owner?.company?.name
    if (company) {
        return t("owner_value_company", {
            firstname,
            lastname,
            company,
        })
    }
    return t("owner_value_self", { firstname, lastname })
})

const addressFormatted = computed(() => formatAddress(props.building?.address))

const idsourceLabel = computed(() =>
    props.source?.idtype === "pce"
        ? t("idsource_label_pce")
        : t("idsource_label_pdl")
)

const idsourceFormatted = computed(() =>
    props.source
        ? formatIdSource(props.source.idsource, props.source.idtype)
        : ""
)

/**
 * Retourne les lignes du résumé (titulaire, adresse, PDL/PCE, dates)
 */
const summaryLines = computed<BaseSummaryLine[]>(() => {
    const lines: BaseSummaryLine[] = [
        // Titulaire
        { label: getOwnerLabel.value, value: getOwnerValue.value },
        // Adresse
        { label: t("address_label"), value: addressFormatted.value },
        // PDL/PCE
        { label: idsourceLabel.value, value: idsourceFormatted.value },
        {
            label: t("start_date_label"),
            value: formatDate(props.dateFrom),
        },
    ]
    // Date d'ouverture du contrat avec le fournisseur (PCE uniquement)
    if (props.source?.idtype === "pce" && props.source?.date_from_data) {
        lines.push({
            label: t("date_from_data_label"),
            value: formatDate(props.source.date_from_data),
        })
    }
    return lines
})

/**
 * Retourne la durée du consentement (défaut 5 ans)
 */
const requestDuration = computed(() => props.request?.duration ?? 5 * 12)

// ================================
// SIGN LOGIC
// ================================

/**
 * Exécute la signature d'un nouveau consentement (création puis mise à jour).
 *
 * Dans le cas où la demande de consentement n'existe pas, on la crée.
 * Dans le cas où la demande de consentement existe, on la met à jour.
 *
 * Informations supplémentaires :
 * - mail : si le destinataire de la demande de consentement est le même que l'expéditeur, on n'envoie pas de mail.
 * - disable_task : même sémantique que le PUT métier. Par défaut false si omis : l’API peut lancer la tâche `building.onboarding.user`.
 *
 * @returns Payload à émettre (consentId, sourceId, token, taskOnboarding)
 */
async function executeSignConsent(): Promise<SignedPayload> {
    const id = props.request._id_consentrequest
    let created: ConsentRequestSignCreated | null = null

    if (!id) {
        created = (await consentRequestService.createConsentRequestSign({
            titulaire: props.owner,
            source: {
                idsource: props.source.idsource,
                idtype: props.source.idtype,
            },
            address: props.building.address,
            buildingId: props.building._id_building,
            dateFromData: props.source.date_from_data ?? null,
            isPro: !!props.owner?.company?.name,
        })) as ConsentRequestSignCreated | null
    }

    const result: SignedPayload = {}
    if (created) {
        result.consentId = created.consentement?._id
        result.sourceId = created.source?._id
        result.token = created.token
        result.taskOnboarding = created.task_building
    }

    const requestId = id ?? created?._id
    if (!requestId) {
        throw new Error(
            "Réponse de création de demande de consentement invalide (id manquant)."
        )
    }

    // Explication des choix :
    // - On vérifie si les utilisateurs source et destination sont identiques via isSameUser.
    //   Cela permet de contrôler si un mail doit être envoyé pour la signature ou non (auto-validation ?).
    // - Si la demande vient d'être créée (created défini), on envoie mail: !isSameUser, sinon on propage la valeur existante props.request.mail.
    // - disable_task : aligné sur le PUT métier (props.request.disable_task ?? false).

    const isSameUser =
        created?.user_from?.username === created?.user_to?.username

    // Si created existe, mail = !isSameUser (n'envoie pas de mail si même utilisateur), sinon garde la valeur actuelle.
    const mailValue = created ? !isSameUser : props.request.mail
    const disableTaskValue = props.request.disable_task ?? false

    const updated = (await consentRequestService.updateConsentRequestSign(
        requestId,
        {
            mail: mailValue,
            disable_task: disableTaskValue,
        }
    )) as ConsentRequestSignCreated | undefined

    result.consentId = updated?.consentement?._id ?? result.consentId
    result.sourceId = updated?.source?._id ?? result.sourceId
    result.token = updated?.token ?? result.token
    result.taskOnboarding = updated?.task_building ?? result.taskOnboarding
    return result
}

/**
 * Exécute la prolongation d'un consentement existant.
 * @returns Payload à émettre (consentId, sourceId)
 */
async function executeExtendConsent(): Promise<SignedPayload> {
    const sourceId = props.source._id_source
    if (!sourceId) throw new Error("source._id_source requis pour EXTEND")

    let requestId = props.request._id_consentrequest
    if (!requestId) {
        const created = await consentRequestService.createConsentRequestExtend(
            sourceId,
            props.owner.user._id!,
            { mail: props.request.mail }
        )
        requestId = (created as { _id?: string })._id!
    }

    const updated = await consentRequestService.updateConsentRequestExtend(
        requestId,
        CONSENT_REQUEST_STATUS.TERMINATED,
        { mail: props.request.mail }
    )

    return {
        consentId: updated?.consentement?._id,
        sourceId: updated?.source?._id,
        consentementFields: pickConsentExtendFieldsFromApiResponse(
            updated?.consentement
        ),
    }
}

/**
 * Exécute le changement de titulaire du consentement.
 * @returns Payload à émettre (consentId, sourceId, token)
 */
async function executeChangeOwnerConsent(): Promise<SignedPayload> {
    const rid = props.request._id_consentrequest
    if (!rid) {
        throw new Error("request._id_consentrequest requis pour CHANGE_OWNER")
    }

    const updated = await consentRequestService.updateConsentRequestChangeOwner(
        rid,
        CONSENT_REQUEST_STATUS.TERMINATED,
        { mail: props.request.mail }
    )

    return {
        consentId: updated?.consentement?._id,
        sourceId: updated?.source?._id,
        token: updated?.token,
    }
}

/**
 * Déclenche l'action de consentement selon le type (signature, prolongation, changement de titulaire)
 * puis émet le résultat signé.
 */
async function performConsentActionAndEmit(): Promise<void> {
    const { type } = props.request
    let result: SignedPayload

    switch (type) {
        case CONSENT_REQUEST_TYPE.SIGN:
            result = await executeSignConsent()
            break
        case CONSENT_REQUEST_TYPE.EXTEND:
            result = await executeExtendConsent()
            break
        case CONSENT_REQUEST_TYPE.CHANGE_OWNER:
            result = await executeChangeOwnerConsent()
            break
        default:
            result = {}
    }

    isDone.value = true
    emit("signed", result)
}

const {
    fetch: fetchSign,
    isFetching,
    error: fetchError,
} = useFetchWrapper(performConsentActionAndEmit)

watch(
    isFetching,
    (signing) => {
        emit("update:signing", signing)
    },
    { immediate: true }
)

const errorMessage = computed(() => {
    const err = fetchError.value
    if (!err) return null
    const detail = (
        err as { response?: { data?: { detail?: { message?: string } } } }
    )?.response?.data?.detail
    return detail?.message ?? err.message ?? "Une erreur est survenue."
})

/** Retour / Fermer : sans `hideBackButton`, toujours la ligne d’actions ; avec `hideBackButton`, uniquement « Fermer » après succès. */
const showSecondaryActionButton = computed(
    () => !props.hideBackButton || isDone.value
)

// ================================
// ACTIONS
// ================================

function onClearError() {
    fetchError.value = null
}

function onBack() {
    if (isDone.value) emit("click:quit")
    else emit("back")
}

function onSubmit() {
    fetchSign()
}
</script>

<template>
    <div class="source-consent-sign flex flex-col items-center gap-6 max-w-3xl">
        <header
            v-if="!props.hideHeaderTitle"
            class="source-consent-sign__header w-full text-center"
        >
            <h2 class="text-lg font-semibold text-gray-700 text-center">
                {{ t("title") }}
            </h2>
        </header>

        <div
            v-if="!isFetching && !isDone"
            class="source-consent-sign__content flex w-full flex-col gap-6"
        >
            <BaseSummary
                :lines="summaryLines"
                variant="default"
                class="w-full"
            />
            <SourceConsentSignConditions
                v-model="signed"
                :type="props.source?.idtype === 'pce' ? 'pce' : 'pdl'"
                :duration="requestDuration"
                :date-from="props.dateFrom"
            />
        </div>

        <div
            v-if="isFetching"
            class="source-consent-sign__loading flex flex-col items-center gap-4 py-8"
        >
            <BaseProgressSpinner style="width: 2.5rem; height: 2.5rem" />
            <p class="text-sm text-gray-600">
                {{
                    props.request?.type === CONSENT_REQUEST_TYPE.EXTEND
                        ? t("loading_extend")
                        : t("loading")
                }}
            </p>
        </div>

        <div
            v-if="!isFetching || isDone"
            class="source-consent-sign__buttons flex flex-wrap gap-3 w-full"
            :class="
                showSecondaryActionButton ? 'justify-between' : 'justify-center'
            "
        >
            <BaseButton
                v-if="showSecondaryActionButton"
                :label="isDone ? t('quitDone') : t('back')"
                severity="secondary"
                class="min-w-[10rem]"
                @click="onBack"
                :disabled="isFetching && !isDone"
            />
            <BaseButton
                v-if="!isDone"
                :label="t('submit')"
                :disabled="!signed || isFetching"
                :loading="isFetching"
                class="min-w-[10rem]"
                @click="onSubmit"
            />
        </div>
        <BaseMessage
            v-if="errorMessage && !isFetching && !isDone"
            severity="error"
            class="w-full"
            :closable="true"
            @close="onClearError"
        >
            {{ errorMessage }}
        </BaseMessage>
    </div>
</template>
